import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { MunicipalityDocument } from '@api/models/municipality-document.model';
import { IndexResponse } from '@api/models/response.model';
import { AppService } from '../../app.service';
import { AuthService } from '@auth/services/auth.service';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { CardComponent } from '@ui/card/card.component';
import { ButtonComponent } from '@ui/button/button.component';
import { ModalService } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { LoadingService } from '@ui/app-layout/loading-service';
import { UploadMunicipalityDocumentModalComponent } from './upload-municipality-document-modal.component';

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }
  return `${(kb / 1024).toFixed(1)} MB`;
}

@Component({
  selector: 'app-municipality-documents',
  standalone: true,
  imports: [CommonModule, TableComponent, CardComponent, ButtonComponent],
  template: `
    <div
      class="flex flex-col gap-4 h-[calc(100vh-theme(spacing.24))] overflow-hidden"
    >
      @if (!municipalityId()) {
        <ui-card>
          <p class="text-sm text-slate-600">
            Selecteer eerst een gemeente (bovenaan) om documenten te bekijken.
          </p>
        </ui-card>
      } @else {
        <ui-card>
          <div class="flex flex-wrap justify-between items-start gap-4">
            <div class="text-sm text-slate-600 max-w-3xl">
              @if (canManage()) {
                <p>
                  Als medewerker van Smienk kunt u hier het
                  <strong class="font-medium text-slate-800">werkafsprakenboek</strong>
                  en andere documenten die u met deze gemeente wilt delen uploaden of verwijderen.
                </p>
              } @else {
                <p>
                  Hier vindt u het
                  <strong class="font-medium text-slate-800">werkafsprakenboek</strong>
                  en andere documenten die in het kader van de samenwerking met Smienk
                  <strong class="font-medium text-slate-800">met u worden gedeeld</strong>.
                  U kunt ze bekijken en downloaden.
                </p>
              }
            </div>
            @if (canManage()) {
              <ui-button
                [icon]="'upload'"
                [disabled]="!municipalityId()"
                (onClick)="openUpload()"
                class="shrink-0"
              >
                Document uploaden
              </ui-button>
            }
          </div>
        </ui-card>
        <div class="h-full overflow-hidden">
          <ui-table
            [columns]="columns()"
            [data]="documents()"
            [loading]="loading()"
            [showPagination]="true"
            [meta]="meta()"
            [bordered]="false"
            emptyMessage="Geen documenten gevonden"
            (onRowClick)="onRowClick($event)"
            (onPageChange)="onPageChange($event)"
          ></ui-table>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MunicipalityDocumentsComponent {
  private municipalitiesService = inject(MunicipalitiesService);
  private appService = inject(AppService);
  private authService = inject(AuthService);
  private modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private confirmService = inject(ConfirmService);
  private loadingService = inject(LoadingService);

  municipalityId = this.appService.municipalityId;
  canManage = this.authService.canManageMunicipalityDocuments;

  documents = signal<MunicipalityDocument[]>([]);
  loading = signal(false);
  meta = signal<IndexResponse<MunicipalityDocument>['meta'] | null>(null);

  columns = computed<TableColumn[]>(() => {
    const canManageDocs = this.canManage();
    const actions: TableColumn['actions'] = [
      {
        label: 'Downloaden',
        icon: 'download',
        hideLabel: true,
        variant: 'secondary',
        onClick: (row: MunicipalityDocument) => this.download(row),
      },
    ];
    if (canManageDocs) {
      actions.push({
        label: 'Verwijderen',
        icon: 'delete',
        hideLabel: true,
        variant: 'danger',
        onClick: (row: MunicipalityDocument) => this.confirmDelete(row),
      });
    }

    return [
      { key: 'original_filename', label: 'Bestand' },
      {
        key: 'size_bytes',
        label: 'Grootte',
        align: 'right',
        format: (v: number) => formatBytes(v),
      },
      {
        key: 'created_at',
        label: 'Geüpload op',
        align: 'right',
        format: (v: string) =>
          new Date(v).toLocaleString('nl-NL', {
            dateStyle: 'short',
            timeStyle: 'short',
          }),
      },
      {
        key: 'uploaded_by.name',
        label: 'Geüpload door',
        format: (_: unknown, row: MunicipalityDocument) =>
          row.uploaded_by?.name ?? '—',
      },
      { key: '_actions', label: '', align: 'right', width: '120px', actions },
    ];
  });

  constructor() {
    effect(
      () => {
        const id = this.appService.municipalityId();
        if (id) {
          this.loadPage(1);
        } else {
          this.documents.set([]);
          this.meta.set(null);
        }
      },
      { allowSignalWrites: true }
    );
  }

  loadPage(page: number) {
    const id = this.appService.municipalityId();
    if (!id) {
      return;
    }
    this.loading.set(true);
    this.municipalitiesService.getDocuments(id, page, 25).subscribe({
      next: (res) => {
        this.documents.set(res.data);
        this.meta.set(res.meta);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.notificationService.error('Documenten laden mislukt.');
      },
    });
  }

  onPageChange(page: number) {
    this.loadPage(page);
  }

  onRowClick(row: MunicipalityDocument) {
    this.download(row);
  }

  openUpload() {
    const id = this.appService.municipalityId();
    if (!id) {
      return;
    }
    const ref = this.modalService.open(UploadMunicipalityDocumentModalComponent, {
      data: { municipalityId: id },
    });
    ref.onClose.subscribe(() => this.loadPage(this.meta()?.current_page ?? 1));
  }

  download(doc: MunicipalityDocument) {
    const mid = this.appService.municipalityId();
    if (!mid) {
      return;
    }
    this.loadingService.show();
    this.municipalitiesService.downloadDocument(mid, doc.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.loadingService.hide();
      },
      error: () => {
        this.loadingService.hide();
        this.notificationService.error('Download mislukt.');
      },
    });
  }

  confirmDelete(doc: MunicipalityDocument) {
    const mid = this.appService.municipalityId();
    if (!mid) {
      return;
    }
    this.confirmService
      .confirm({
        title: 'Document verwijderen',
        message: `Weet u zeker dat u "${doc.original_filename}" wilt verwijderen?`,
        confirmText: 'Verwijderen',
        variant: 'danger',
      })
      .then((ok) => {
        if (!ok) {
          return;
        }
        this.municipalitiesService.deleteDocument(mid, doc.id).subscribe({
          next: () => {
            this.notificationService.success('Document verwijderd.');
            this.loadPage(this.meta()?.current_page ?? 1);
          },
          error: () => {
            this.notificationService.error('Verwijderen mislukt.');
          },
        });
      });
  }
}
